import { Link, useLocation, useSearchParams } from 'react-router-dom'

import type { MapView } from './holeGeometry'

/**
 * Switch between the drawn map and the aerial photo -- offered ONLY when a
 * hole has both, because a switch with one position is a control that does
 * nothing.
 *
 * # Links, not buttons
 *
 * Public views carry no buttons, inputs or forms (FR-047c; public.spec.ts
 * asserts it on every one), and a link is the better control here anyway: the
 * view is in the address (`?view=aerial`), so it survives the hole view's
 * polling and a reader can send somebody the photo rather than the diagram.
 * Same shape as the statistics filters.
 */
export function MapViewSwitch({ view }: { view: MapView }) {
  const [params] = useSearchParams()
  const { pathname } = useLocation()
  const href = (v: MapView) => {
    const next = new URLSearchParams(params)
    next.set('view', v)
    return `${pathname}?${next.toString()}`
  }
  return (
    <nav className="map-view-switch" aria-label="Map view">
      {(['aerial', 'drawn'] as const).map((v) => (
        <Link
          key={v}
          to={href(v)}
          replace
          className={view === v ? 'current' : undefined}
          aria-current={view === v ? 'true' : undefined}
          data-testid={`map-view-${v}`}
        >
          {v === 'aerial' ? 'Photo' : 'Map'}
        </Link>
      ))}
    </nav>
  )
}
